import { observer } from "mobx-react-lite";
import { ReactElement, useEffect, useRef, useState } from "react";
import { BoxProps, Input, InputGroup, InputLeftElement, InputProps, Text } from "@chakra-ui/react";

import { Dictionary, isString, isUndefined } from "@noli/core";

import { themeStore } from "@/stores/theme";
import getInputProps from "@/styles/inputProps";
import TextArea, { TextAreaProps } from "@/components/TextArea";

export interface IProperty<T extends string | number> extends BoxProps {
  identifier?: string;
  noTitle?: boolean;
  icon?: ReactElement;
  text?: string;
  value: T;
  setValue: (value: T) => void;
  captionW?: string;
  inputPadding?: string;
  inputPaddingRight?: string;
  inputProps?: InputProps;
  lower?: number;
  upper?: number;
  textAreaProps?: Omit<TextAreaProps, "text" | "setText">;
}

function getValidNumber(
  v: string,
  { lower, upper }: { lower: number | undefined; upper: number | undefined },
): number | undefined {
  let value = parseFloat(v);
  if (!Number.isNaN(value)) {
    if (!isUndefined(lower)) {
      value = Math.max(lower, value);
    }
    if (!isUndefined(upper)) {
      value = Math.min(upper, value);
    }
    return value;
  }
}

function Property<T extends string | number>({
  identifier,
  noTitle,
  icon,
  text,
  value,
  setValue,
  captionW,
  inputPadding,
  inputPaddingRight,
  inputProps,
  lower,
  upper,
  placeholder,
  _placeholder,
  textAreaProps,
  ...others
}: IProperty<T>) {
  const { captionColor } = themeStore.styles;
  const useNumber = !isString(value);
  const caption = icon ?? <Text userSelect="none">{text ?? ""}</Text>;

  const ref = useRef<Dictionary<string | undefined>>({});
  const setLocalV = useState<string | undefined>("")[1];
  identifier ??= "";
  const originalKey = `${identifier}.original`;
  const stringValue = `${value}`;
  if (isUndefined(ref.current[identifier]) || ref.current[originalKey] !== stringValue) {
    ref.current[identifier] = stringValue;
  }
  ref.current[originalKey] = stringValue;
  captionW ??= "35%";

  function onChange(v: string): void {
    setLocalV(v);
    ref.current[identifier as string] = v;
  }

  function onSet(): void {
    const v = ref.current[identifier as string];
    const originalV = ref.current[originalKey];
    if (!isUndefined(v) && v !== originalV) {
      setValue((useNumber ? getValidNumber(v, { lower, upper }) ?? value : v) as T);
    }
    setLocalV(undefined);
    ref.current[identifier as string] = undefined;
  }

  useEffect(() => onSet, [identifier]);

  if (textAreaProps) {
    return (
      <TextArea
        {...textAreaProps}
        onBlur={onSet}
        text={ref.current[identifier as string] ?? ""}
        setText={onChange}
      />
    );
  }

  return (
    <InputGroup onBlur={onSet} {...others}>
      {noTitle ? null : (
        <InputLeftElement w={captionW} h="100%" fontSize="12px" color={captionColor}>
          {caption}
        </InputLeftElement>
      )}
      <Input
        pl={inputPadding ?? captionW}
        pr={inputPaddingRight ?? "8px"}
        variant="unstyled"
        value={ref.current[identifier]}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        _placeholder={_placeholder}
        {...getInputProps()}
        {...(inputProps ?? {})}
      />
    </InputGroup>
  );
}

export default observer(Property);
